import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { MarketplaceExtractAttribute } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_marketplace_pb";

import {
  createMarketplaceExtractAttribute,
  deleteMarketplaceExtractAttribute,
  getMarketplaceExtractAttributes,
} from "api/endpoints/marketplace";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import AddExtractAttributeForm, {
  NewAttributeFormState,
} from "components/marketplace/add-attribute-form";
import { DEFAULT_REGION_CODE } from "constants/index";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

type ProductAttributeRow = Pick<
  MarketplaceExtractAttribute,
  "id" | "name" | "description"
>;

const defaultFormState: NewAttributeFormState = {
  name: "",
  description: "",
};

const MarketplaceAttributes = () => {
  const { showError } = useContext(SnackbarContext);
  const [openAddAttributeDialog, setOpenAddAttributeDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [attributesData, setAttributesData] = useState<ProductAttributeRow[]>(
    []
  );
  const [newAttributeFormState, setNewAttributeFormState] =
    useState(defaultFormState);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const attributesData = await getMarketplaceExtractAttributes(
        callOptionsHeaders(DEFAULT_REGION_CODE)
      );
      setIsLoading(false);
      setAttributesData(attributesData.attributes);
    };
    init();
  }, []);

  const handleAddAttribute = async () => {
    setIsLoading(true);
    try {
      const attributeData = await createMarketplaceExtractAttribute(
        {
          name: newAttributeFormState.name,
          description: newAttributeFormState.description,
        },
        callOptionsHeaders(DEFAULT_REGION_CODE)
      );
      const attribute = attributeData.attribute;

      if (!attribute) {
        showError({
          code: gRPCStatus.UNKNOWN,
          name: "No attribute returned",
          message:
            "Unknown error occurred adding a marketplace attribute. Please try again later.",
        });
      } else {
        const fetchedAttributeData = await getMarketplaceExtractAttributes(
          callOptionsHeaders(DEFAULT_REGION_CODE)
        );
        setAttributesData(fetchedAttributeData.attributes);
      }
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setOpenAddAttributeDialog(false);
      setNewAttributeFormState(defaultFormState);
      setIsLoading(false);
    }
  };

  const handleDeleteAttribute = (attributeId: string) => {
    deleteMarketplaceExtractAttribute(
      { attributeId },
      callOptionsHeaders(DEFAULT_REGION_CODE)
    )
      .then(() => {
        setAttributesData(
          attributesData.filter((attr) => attr.id !== attributeId)
        );
      })
      .catch((error) => {
        showError(error as GrpcError);
      });
  };

  const renderAttributeRecord = (row: ProductAttributeRow) => (
    <TableRow key={`run-${row.id}`}>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.name}
      </TableCell>
      <TableCell align="left" style={{ verticalAlign: "top" }}>
        {row.description}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        <Button
          data-testid={`delete-attribute-${row.id}`}
          onClick={() => {
            handleDeleteAttribute(row.id);
          }}
        >
          Delete
        </Button>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "20px" }}>
          <Grid container alignContent="space-between" alignItems="center">
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Typography
                  variant="h5"
                  color="textPrimary"
                  gutterBottom
                  data-testid="ai-tool-job-runs-title"
                >
                  Extract Attributes
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setOpenAddAttributeDialog(true)}
                  data-testid="add-attribute-dialog-btn"
                >
                  Add Attribute
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table aria-label="crowndin-templates">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <b>Name</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Description</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  attributesData.map((row) => renderAttributeRecord(row))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <AddExtractAttributeForm
        isOpen={openAddAttributeDialog}
        handleClose={() => {
          setOpenAddAttributeDialog(false);
        }}
        newAttributeFormState={newAttributeFormState}
        setNewAttributeFormState={setNewAttributeFormState}
        handleSubmit={() => {
          setIsLoading(true);
          handleAddAttribute();
        }}
        isLoading={isLoading}
        isDisabled={
          !newAttributeFormState.name || !newAttributeFormState.description
        }
      />
    </>
  );
};

export default MarketplaceAttributes;
